import { useMemo } from 'react';
import RequirementPills, { type TaskRequirements } from './RequirementPills';
import { othersWhoCovered, storeVisitsFor } from '../utils/coverage';

// Haversine formula for distance calculation
function calculateDistance(lat1: number, lon1: number, lat2: number | null, lon2: number | null): number | null {
  if (lat2 === null || lon2 === null) return null;

  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
}

function kmToMiles(km: number): number {
  return km * 0.621371;
}

// Progress bar component
function ProgressBar({ completed, total }: { completed: number; total: number }) {
  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 flex-1 rounded-full bg-slate-200">
        <div
          className={`h-2 rounded-full transition-all ${
            percentage >= 100 ? 'bg-green-500' : percentage >= 50 ? 'bg-cyan-500' : 'bg-amber-500'
          }`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>
      <span className="text-xs font-medium text-slate-600">{percentage}%</span>
    </div>
  );
}

export interface TaskCardStore {
  store_id?: number;
  store_name?: string;
  legacy_store_id?: number;
  address?: string;
  city?: string;
  state?: string;
  retailer_abbreviation?: string;
  lat?: number | null;
  lon?: number | null;
}

export interface TaskCardTeamMember {
  associate_id: number;
  name: string;
  is_current_user?: boolean;
  role?: string;
}

export interface TaskCardProps {
  // Common fields
  type: 'set' | 'call';
  store: TaskCardStore;
  projectId?: number;
  projectDescription?: string;
  status?: 'pending' | 'completed' | 'overdue';
  onClick?: () => void;

  // Set-specific fields
  setDate?: Date | string;
  setHours?: number;
  windowStart?: string;
  windowEnd?: string;
  team?: TaskCardTeamMember[];
  setTypeName?: string;

  // Call-specific fields
  visitsCompleted?: number;
  visitsRequired?: number;
  /**
   * Visits at this store counted across EVERY rep (#3454). The schedule is
   * per-associate, so a colleague covering this call is otherwise invisible
   * and two reps drive to the same store. Undefined on surfaces that haven't
   * opted in, which then render exactly as before.
   */
  visitsCompletedStore?: number;
  /**
   * OTHER reps who covered this call this month, most recent first. The API
   * reports every contributor including the current rep, so callers pass this
   * through `othersWhoCovered()` — a card that named the rep back to
   * themselves would read as someone else having been there.
   */
  completedBy?: { name: string; count: number; last?: string | null }[];
  hoursPerVisit?: number;
  lastVisitDate?: string;
  /** Pre-formatted duration label. When set, replaces the `{hours}h` render
   *  in the info row. Use for completed tasks so a 1-minute visit reads
   *  "1m" instead of "0.0166666666666666h". (#1954 follow-up) */
  durationLabel?: string;

  // Per-project onsite requirements → Ord/Inv/OOS/Photo/Doc pills (#2214).
  requirements?: TaskRequirements;

  // Display options
  showIndex?: number;
  isActive?: boolean;
  userLocation?: { lat: number; lng: number } | null;
}

export default function TaskCard({
  type,
  store,
  projectId,
  projectDescription,
  status = 'pending',
  onClick,
  setDate,
  setHours,
  windowStart,
  windowEnd,
  team,
  setTypeName,
  visitsCompleted,
  visitsRequired,
  visitsCompletedStore,
  completedBy,
  hoursPerVisit,
  lastVisitDate,
  durationLabel,
  requirements,
  showIndex,
  isActive = false,
  userLocation,
}: TaskCardProps) {
  // Coverage of this call at this store. Falls back to the rep's own count so
  // a surface that hasn't opted in renders exactly as it did before.
  const storeVisits = storeVisitsFor({ visits_completed_store: visitsCompletedStore }, visitsCompleted ?? 0);
  // Callers pass others only; guard against an empty contributor anyway.
  const othersCovered = useMemo(() => othersWhoCovered(completedBy, null), [completedBy]);

  // Calculate distance
  const distanceMiles = useMemo(() => {
    if (!userLocation || store.lat === null || store.lat === undefined ||
        store.lon === null || store.lon === undefined) {
      return null;
    }
    const distanceKm = calculateDistance(userLocation.lat, userLocation.lng, store.lat, store.lon);
    if (distanceKm === null) return null;
    const miles = kmToMiles(distanceKm);
    if (miles < 0.1) return '< 0.1 mi';
    return `${miles.toFixed(1)} mi`;
  }, [userLocation, store.lat, store.lon]);

  // Get store display name
  const storeName = store.store_name ||
    `${store.retailer_abbreviation || ''} #${store.legacy_store_id || store.store_id}`.trim();

  // Format date
  const formattedDate = useMemo(() => {
    if (!setDate) return null;
    const date = typeof setDate === 'string' ? new Date(setDate.split('T')[0] + 'T12:00:00') : setDate;
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  }, [setDate]);

  // Format last visit date
  const formattedLastVisit = useMemo(() => {
    if (!lastVisitDate) return 'Never';
    // Append T12:00:00 to avoid UTC midnight → local timezone off-by-one
    const dateStr = lastVisitDate.split('T')[0];
    const date = new Date(dateStr + 'T12:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }, [lastVisitDate]);

  // Determine border color based on status and type
  const getBorderColor = () => {
    if (isActive) return 'border-green-500 ring-2 ring-green-200';
    if (status === 'completed') return 'border-green-500';
    if (status === 'overdue') return 'border-red-500';
    return type === 'set' ? 'border-amber-500' : 'border-cyan-500';
  };

  // Badge colors
  const getBadgeColors = () => {
    if (status === 'completed') return 'bg-green-100 text-green-700';
    if (status === 'overdue') return 'bg-red-100 text-red-700';
    return type === 'set' ? 'bg-amber-100 text-amber-700' : 'bg-cyan-100 text-cyan-700';
  };

  // Type label
  const typeLabel = type === 'set' ? 'Set' : 'Call';
  const hours = setHours || hoursPerVisit;

  return (
    <button
      onClick={onClick}
      className={`w-full rounded-xl bg-white p-4 text-left shadow-card transition-all hover:shadow-card-hover active:scale-[0.98] border-l-4 ${getBorderColor()}`}
    >
      {/* Main content row */}
      <div className="flex items-start justify-between">
        {/* Index number if provided */}
        {showIndex !== undefined && (
          <div className={`mr-3 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold ${getBadgeColors()}`}>
            {showIndex}
          </div>
        )}

        <div className="flex-1 min-w-0">
          {/* Store name row */}
          <div className="flex items-center gap-2">
            <h3 className="font-heading font-semibold text-slate-800 truncate">
              {storeName}
            </h3>
            {isActive && (
              <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 flex-shrink-0">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-green-500"></span>
                Active
              </span>
            )}
            {status === 'completed' && (
              <svg className="h-4 w-4 flex-shrink-0 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            )}
          </div>

          {/* Address */}
          {(store.address || store.city) && (
            <p className="mt-1 text-sm text-slate-500 truncate">
              {store.address}{store.address && store.city ? ', ' : ''}{store.city}
              {store.state && `, ${store.state}`}
            </p>
          )}

          {/* Project description */}
          {projectDescription && (
            <p className="mt-1 text-xs text-slate-400 truncate">
              {projectId && <span className="font-mono text-primary-600">#{projectId} </span>}
              {projectDescription}
            </p>
          )}

          {/* Info row with badges */}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {/* Type/Status badge */}
            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${getBadgeColors()}`}>
              {status === 'overdue' ? 'Overdue' : typeLabel}
            </span>

            {/* Hours — durationLabel (pre-formatted) wins if set, else
                `{hours}h` for the legacy set/call hours-per-visit values. */}
            {durationLabel ? (
              <span className="text-xs text-slate-500">{durationLabel}</span>
            ) : hours ? (
              <span className="text-xs text-slate-500">{hours}h</span>
            ) : null}

            {/* Date for sets */}
            {formattedDate && type === 'set' && (
              <span className="text-xs text-slate-500">{formattedDate}</span>
            )}

            {/* Visit count for calls. Counts the store when that number is
                available, so a call a colleague already covered reads as
                covered rather than as untouched work. */}
            {type === 'call' && visitsRequired !== undefined && (
              <span className={`text-xs font-medium ${
                storeVisits >= visitsRequired ? 'text-green-600' : 'text-slate-600'
              }`}>
                {storeVisits}/{visitsRequired} visits
              </span>
            )}

            {/* Requirement pills (#2214) — what this task needs onsite. */}
            <RequirementPills requirements={requirements} />
          </div>

          {/* Window dates for sets - always show if available */}
          {type === 'set' && (windowStart || windowEnd) && (
            <div className="mt-2 text-xs text-slate-500">
              Window: <span className="font-medium">{windowStart || '?'} - {windowEnd || '?'}</span>
            </div>
          )}

          {/* Set type name if available */}
          {type === 'set' && setTypeName && (
            <div className="mt-1 text-xs text-slate-500">
              Type: <span className="font-medium text-slate-700">{setTypeName}</span>
            </div>
          )}

          {/* Who else has covered this call. Named and frequency-aware: "1 of 3
              done" still needs work, "3 of 3" does not, and either way the rep
              can see it was someone else rather than assuming it was them.
              Best-effort — a visit that hasn't synced yet won't appear. */}
          {type === 'call' && othersCovered.length > 0 && (
            <div className="mt-2 flex items-start gap-1.5 text-xs text-amber-700">
              <svg className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span>
                Also visited by {othersCovered.map((c) => c.name).join(', ')}
              </span>
            </div>
          )}

          {/* Progress bar for calls */}
          {type === 'call' && visitsRequired !== undefined && visitsRequired > 0 && (
            <div className="mt-3">
              <ProgressBar completed={storeVisits} total={visitsRequired} />
            </div>
          )}

          {/* Last visit info for calls */}
          {type === 'call' && (
            <div className="mt-2 text-xs text-slate-500">
              Last visit: <span className="font-medium text-slate-700">{formattedLastVisit}</span>
            </div>
          )}

          {/* Team members for sets - always show if multiple */}
          {type === 'set' && team && team.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {team.slice(0, 4).map((member) => (
                <span
                  key={member.associate_id}
                  className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                    member.is_current_user
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  {member.name.split(' ')[0]}
                  {member.is_current_user && ' (You)'}
                </span>
              ))}
              {team.length > 4 && (
                <span className="text-xs text-slate-400">+{team.length - 4}</span>
              )}
            </div>
          )}
        </div>

        {/* Right side: distance and arrow */}
        <div className="text-right flex-shrink-0 ml-3">
          {distanceMiles && (
            <div className={`text-lg font-bold ${type === 'set' ? 'text-amber-600' : 'text-cyan-600'}`}>
              {distanceMiles}
            </div>
          )}
          <svg className="h-5 w-5 text-slate-400 ml-auto mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </button>
  );
}
